import { BotId } from '@feedyou/schema'
import {
  ActiveRunInfo,
  BotTesterRunRecord,
  BotTesterScenario,
  BotTesterScenarioWithLastRun,
  BotTestRunResult,
  collapseUnion,
  TestRunConfig,
  TestRunDescriptor
} from '@feedyou/utils'

import { serverApi, ServerApiTag } from '../../store/serverApi'
import { bindSideEffects } from '../../lib/rtkq/bindSideEffects'
import {
  toastNotifyCreateScenarioFailure,
  toastNotifyCreateScenarioSuccess,
  toastNotifyRunError
} from './toasting'
import { StoreApi } from '../../store/types'

export const botTesterApi = serverApi.injectEndpoints({
  endpoints: builder => ({
    runBotTest: builder.mutation<ActiveRunInfo<any>, TestRunConfig<any>>({
      query: params => ({
        url: `bots/${params.botId}/bot-tester/active-runs`,
        method: 'post',
        body: { config: params }
      }),
      onQueryStarted: bindSideEffects({
        failure: [(params, _, api) => toastNotifyRunError(params, api as unknown as StoreApi)]
      }),
      invalidatesTags: (res, error, params) => {
        return [ServerApiTag.BOT_TESTER_RECORDED_RUNS(collapseUnion(params))]
      }
    }),

    createTesterScenario: builder.mutation<
      void,
      { botId: string; conversationId: string; name: string }
    >({
      query: ({ botId, ...body }) => ({
        url: `bots/${botId}/bot-tester/scenarios`,
        method: 'post',
        body: { ...body }
      }),
      onQueryStarted: bindSideEffects({
        success: [(params, _, api) => toastNotifyCreateScenarioSuccess(params.botId)],
        failure: [(params, _, api) => toastNotifyCreateScenarioFailure()]
      }),
      invalidatesTags: (res, err, { botId }) => [ServerApiTag.BOT_TESTER_SCENARIOS(botId)]
    }),

    getTesterScenarios: builder.query<BotTesterScenarioWithLastRun[], { botId: BotId }>({
      query: params => ({
        url: `bots/${params.botId}/bot-tester/scenarios`,
        method: 'get'
      }),
      transformResponse: (ret: { scenarios: BotTesterScenarioWithLastRun[] }) => ret.scenarios,
      providesTags: (res, err, { botId }) => [ServerApiTag.BOT_TESTER_SCENARIOS(botId)]
    }),

    deleteTesterScenario: builder.mutation<void, { botId: BotId; scenarioId: string }>({
      query: params => ({
        url: `bots/${params.botId}/bot-tester/scenarios/${params.scenarioId}`,
        method: 'delete'
      }),
      invalidatesTags: (res, err, { botId }) => [ServerApiTag.BOT_TESTER_SCENARIOS(botId)]
    }),

    modifyTesterScenario: builder.mutation<
      void,
      {
        botId: BotId
        scenarioId: string
        patch: Partial<
          Pick<
            BotTesterScenario,
            'name' | 'testedUserId' | 'runDaily' | 'notificationEmails' | 'sendEmailNotifications'
          >
        >
      }
    >({
      query: params => ({
        url: `bots/${params.botId}/bot-tester/scenarios/${params.scenarioId}`,
        method: 'PATCH',
        body: { ...params.patch }
      }),
      invalidatesTags: (res, err, { botId }) => [ServerApiTag.BOT_TESTER_SCENARIOS(botId)]
    }),

    getRecordedRunsList: builder.query<
      BotTesterRunRecord[],
      { config: TestRunConfig<any>; count?: number }
    >({
      query: ({ config, count = 20 }) => ({
        url: `bots/${config.botId}/bot-tester/recorded-runs`,
        method: 'get',
        params: { count, ...config }
      }),
      transformResponse: (ret: { runs: BotTesterRunRecord[] }) => ret.runs,
      providesTags: (res, err, { config }) => {
        return [ServerApiTag.BOT_TESTER_RECORDED_RUNS(collapseUnion(config))]
      }
    }),

    getRecordedRunWithDetails: builder.query<
      BotTesterRunRecord & { fullResult: BotTestRunResult },
      TestRunDescriptor<any>
    >({
      query: ({ botId, reversedMicroTimestamp, ...config }) => ({
        url: `bots/${botId}/bot-tester/recorded-runs/${reversedMicroTimestamp}`,
        method: 'get',
        params: { ...config }
      }),
      providesTags: (res, err, params) => [
        ServerApiTag.BOT_TESTER_RECORDED_RUN(collapseUnion(params))
      ]
    })
  })
})
